import { createForm, useCreateFormStore } from '@components/form';
import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconEdit } from '@components/icons/edit';
import { Spinner } from '@components/loading-spinner';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';
import { createNumberOptions, hourList } from '@utils/number-arrays';

import {
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { TravellerJourneyActionDetailed } from 'api/types';
import { getActionStatus } from 'ui/authenticated/main/helpers';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';

import { ACTION_TYPES } from '../action-config';
import {
  ActionProgressBar,
  EditableActionContent,
  FormSidebarFooter,
  getWaitForTimeLength,
  isActionError,
  SidebarProps,
  TravellerActionActions,
  TravellerActionHeader,
  useEditingContext,
} from '../common';
import { FormGroupContainer } from '../wait-for/journey-sidebar';

const ACTION_TYPE = ACTION_TYPES.journey_loop;

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const travellerActionQuery = useJourneyTravellerAction<typeof ACTION_TYPE>(shortId);
  if (travellerActionQuery.isLoading) {
    return <Spinner />;
  }
  if (!shortId || !travellerActionQuery.data) {
    return <div>{t('dataNotFound')}</div>;
  }

  const action = travellerActionQuery.data;

  return (
    <Sidebar>
      <TravellerActionHeader actionType={ACTION_TYPE}>
        <TravellerActionActions action={action} />
      </TravellerActionHeader>
      <Sidebar.Content>
        <EditableActionContent
          edit={<FormContent action={action} />}
          display={<DetailsContent action={action} />}
        />
      </Sidebar.Content>
    </Sidebar>
  );
};

type ContentProps = {
  action: TravellerJourneyActionDetailed<typeof ACTION_TYPE>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const isError = isActionError(action);
  const status = getActionStatus(action);
  const { startEditing } = useEditingContext();
  const data = action.data;

  const progressText =
    status === 'completed'
      ? t('completed')
      : isError
      ? t('error')
      : status === 'in_progress'
      ? t('inProgress')
      : t('notStarted');

  return (
    <Stack space={6}>
      <HideContainer title={t('actionStatus')}>
        <ActionProgressBar
          action={action}
          progressText={progressText}
          isError={isError}
          dateSuccess={action.result?.dateSucceeded}
          isCard={false}
          progressPercentage=" "
        />
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('configuration')}>
        <Stack space={4}>
          <div>
            <Text font="regular14" text={t('configurationDescription')} />
            <div>
              {data.days !== '0' && (
                <Text
                  font="semibold14"
                  text={data.days === '1' ? t('dayConfig') : t('daysConfig', { total: data.days })}
                />
              )}
              {data.hours !== '0' && (
                <Text
                  font="semibold14"
                  text={
                    data.hours === '1' ? t('hourConfig') : t('hoursConfig', { total: data.hours })
                  }
                />
              )}
              {data.minutes !== '0' && (
                <Text
                  font="semibold14"
                  text={
                    data.minutes === '1'
                      ? t('minuteConfig')
                      : t('minutesConfig', { total: data.minutes })
                  }
                />
              )}
            </div>
          </div>
          <div>
            <Text font="regular14" text={t('totalRuns')} />
            <Text font="semibold14" text={data.runCounter ?? 0} />
          </div>
          <div>
            <Text
              font="regular14"
              text={action.status === 'completed' ? t('lastRun') : t('nextScheduledRun')}
            />
            {data.date ? (
              <Text font="semibold14" text={abbreviateDate(data.date, 'en', 'datetime')} />
            ) : (
              <Text font="semibold14" text={t('afterSuccess')} />
            )}
          </div>
        </Stack>
      </HideContainer>
      <Sidebar.Separator />
      {status !== 'completed' && (
        <Sidebar.Footer>
          <IconButton variant="ghost-purple" icon={<IconEdit />} onClick={startEditing} />
        </Sidebar.Footer>
      )}
    </Stack>
  );
};

type Data = {
  days: string;
  hours: string;
  minutes: string;
};

const Form = createForm<Data>();

const FormContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { stopEditing } = useEditingContext();
  const formStore = useCreateFormStore();
  const minuteList = createNumberOptions(0, 60);
  const days = formStore.useFieldValue<string>('days') ?? '';
  const hours = formStore.useFieldValue<string>('hours') ?? '';
  const minutes = formStore.useFieldValue<string>('minutes') ?? '';
  const timeLengthText = getWaitForTimeLength(days, hours, minutes);
  const isValid = parseInt(days) > 0 || parseInt(hours) > 0 || parseInt(minutes) >= 10;

  const isDirty = formStore.useIsDirty();
  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });

  const handleSave = (data: Data) => {
    return editMutation.mutateAsync({
      values: {
        days: { value: data.days },
        hours: { value: data.hours },
        minutes: { value: data.minutes },
      },
    });
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={(data) => handleSave(data)}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <Text font="regular14" text={t('loopDescription')} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('loopTimer')}>
          <Stack space={4}>
            <FormGroupContainer>
              <Form.TextField
                id="days"
                label={t('days')}
                defaultValue={action.values?.days?.value ?? '0'}
                validators={['number']}
              />
              <Form.Select
                id="hours"
                label={t('hours')}
                options={hourList}
                placeholder=""
                defaultValue={action.values?.hours?.value ?? '12'}
                isClearable={false}
              />
              <Form.Select
                id="minutes"
                label={t('minutes')}
                options={minuteList}
                placeholder=""
                defaultValue={action.values?.minutes?.value ?? '00'}
                isClearable={false}
              />
            </FormGroupContainer>
            <Text
              text={timeLengthText.length > 0 ? t('loopTimerDescription', { timeLengthText }) : ''}
            />
            {!isValid && (
              <Text font="regular14" color="elementRed100" text={t('loopTimerInvalid')} />
            )}
          </Stack>
        </HideContainer>
        <FormSidebarFooter
          Form={Form}
          isDirty={isDirty}
          editMutation={editMutation}
          hasNotifyError={!isValid}
        />
      </Stack>
    </FormWithUnsavedChangesPrompt>
  );
};

const sidebarLocale = {
  en: {
    actionStatus: 'Action status',
    configuration: 'Configuration',
    completed: 'Completed',
    inProgress: 'In progress',
    error: 'Error',
    notStarted: 'Not started',
    dataNotFound: 'Data not found',
    configurationDescription: 'The loop is configured to restart every',
    totalRuns: 'Total runs:',
    lastRun: 'Last run:',
    nextScheduledRun: 'Next scheduled run:',
    dayConfig: '1 • Day',
    daysConfig: '{total} • Days',
    hourConfig: '1 • Hour',
    hoursConfig: '{total} • Hours',
    minuteConfig: '1 • Minute',
    minutesConfig: '{total} • Minutes',
    selectHowLong:
      'Select for how long journey step should wait before triggreing the following action',
    useWhen: 'Use when you want to pause by a defined amount of time between actions in a journey',
    waitsForDescription: 'Waits for {timeLengthText} before triggering the following action',
    daysPassed: '{days} Days',
    triggersNextAction: 'Triggers next action',
    actionDetails: 'Action details',
    loopTimer: 'Loop timer',
    loopDescription: 'Use when you want to repeat some actions until a condition is met.',
    loopTimerDescription: 'Restart loop every • {timeLengthText}',
    days: 'Days',
    hours: 'Hours',
    minutes: 'Minutes',
    waitsFor: 'Waits for',
    day: 'Day',
    hour: 'Hour',
    minute: 'Minute',
    afterSuccess: 'When preceding action completes',
    loopTimerInvalid: 'Loop cannot run more frequently than every 10 minutes',
  },
};
